import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { FaQrcode } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { Badge } from '@/components/ui/badge';
import { DataTableColumnHeader } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTableColumnHeader';
import ContainerTypeDisplay from '../../components/ContainerType/ContainerTypeDisplay';
import { OverlayPopover } from '../../components/OverlayPopover';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';

const TRANSACTION_TYPE = {
  Checkout: 'checkout',
  Returns: 'returns',
} as const;

/** A single flattened transaction row (one container in one transaction). */
export type TransactionRow = {
  name: string;
  id: string;
  containerTypeImageUrl: string | null;
  typeId: number;
  timestamp: string;
  containerType: ContainerType | undefined;
  type: string;
  isInternal: boolean;
  uniqueId: number;
  userId: string;
  userName: string;
  externalIntegrationUserId: string | null;
  containerLocationId: number;
  shortId?: string;
  /** Resolved container type name; used by the CSV/Excel export. */
  containerTypeName?: string;
  /** Holding state of a checkout row; only set when the tab asked for it (holding-state filter). */
  holdingStatus: number | null;
  holdingReturnDeadline: string | null;
  holdingChargedAt: string | null;
};

export function getTransactionTypeName(transaction: TransactionRow) {
  if (transaction.type === TRANSACTION_TYPE.Checkout) {
    return transaction.isInternal
      ? i18n.t('MerchantTransactions:removal')
      : i18n.t('MerchantTransactions:checkout');
  }
  return transaction.isInternal
    ? i18n.t('MerchantTransactions:entry')
    : i18n.t('MerchantTransactions:return');
}

function renderContainerNameCell(
  id: string,
  name: string,
  canAccessPartnerData: boolean,
) {
  if (!canAccessPartnerData) {
    return name;
  }

  return (
    <Link to={`/container/${id}`}>
      {name}
      <OverlayPopover
        body={
          <img alt="QR code for container" src={getContainerQrCodeImgUrl(id)} />
        }
        header={i18n.t('MerchantTransactions:QRCodeFor', { name })}
      >
        <FaQrcode className="ms-1" style={{ cursor: 'pointer' }} />
      </OverlayPopover>
    </Link>
  );
}

export type GetTransactionColumnsOptions = {
  canAccessPartnerData: boolean;
  canAccessUserData: boolean;
  hasExternalIntegrationUserIds: boolean;
  vytalStoreGuid: string;
  /** URL of the current transactions view, so the drill-down can return here. */
  fromPath: string;
  /** Show the guest's return deadline; only meaningful while the holding-state filter is on. */
  showHoldingState: boolean;
};

export function getTransactionColumns({
  canAccessPartnerData,
  canAccessUserData,
  hasExternalIntegrationUserIds,
  vytalStoreGuid,
  fromPath,
  showHoldingState,
}: GetTransactionColumnsOptions): ColumnDef<TransactionRow>[] {
  const columns: ColumnDef<TransactionRow>[] = [
    {
      id: 'containerType',
      accessorFn: (row) => row.containerType?.name ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:containerType')}
        />
      ),
      cell: ({ row }) => (
        <ContainerTypeDisplay
          containerType={row.original.containerType}
          showContainerId={false}
        />
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:containerType'),
      },
      filterFn: 'arrIncludesSome',
    },
    {
      accessorKey: 'type',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:transactionType')}
        />
      ),
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.type === TRANSACTION_TYPE.Checkout
              ? 'default'
              : 'secondary'
          }
        >
          {getTransactionTypeName(row.original)}
        </Badge>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:transactionType'),
      },
      filterFn: 'arrIncludesSome',
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:containerName')}
        />
      ),
      cell: ({ row }) =>
        renderContainerNameCell(
          row.original.id,
          row.original.name,
          canAccessPartnerData,
        ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:containerName'),
      },
    },
    {
      accessorKey: 'timestamp',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:transactionDate')}
        />
      ),
      cell: ({ row }) => (
        <span title={row.original.timestamp}>
          {new Date(row.original.timestamp).toLocaleDateString(
            i18n.language,
            getDateFormatOptions(true),
          )}
        </span>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:transactionDate'),
      },
    },
    ...(showHoldingState
      ? [
          {
            accessorKey: 'holdingReturnDeadline',
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title={i18n.t('MerchantTransactions:returnDeadline')}
              />
            ),
            cell: ({ row }) =>
              row.original.holdingReturnDeadline ? (
                <span title={row.original.holdingReturnDeadline}>
                  {new Date(
                    row.original.holdingReturnDeadline,
                  ).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )}
                </span>
              ) : (
                '-'
              ),
            meta: {
              getName: () => i18n.t('MerchantTransactions:returnDeadline'),
            },
          } satisfies ColumnDef<TransactionRow>,
        ]
      : []),
    {
      accessorKey: 'id',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:containerId')}
        />
      ),
      cell: ({ row }) => (
        <span className="tw:break-all">{row.original.id}</span>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:containerId'),
      },
    },
    {
      accessorKey: 'shortId',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:shortId')}
        />
      ),
      cell: ({ row }) => (
        <span className="tw:break-all">{row.original.shortId || '-'}</span>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:shortId'),
      },
    },
  ];

  if (hasExternalIntegrationUserIds) {
    columns.push({
      accessorKey: 'externalIntegrationUserId',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:externalIntegrationUserId')}
        />
      ),
      cell: ({ row }) => (
        <div className="tw:flex tw:flex-col">
          <span
            className="tw:break-all"
            title={row.original.externalIntegrationUserId || '-'}
          >
            {row.original.externalIntegrationUserId || '-'}
          </span>
          {row.original.externalIntegrationUserId && (
            <Link
              to={Routes.partnerUserCheckouts(
                row.original.externalIntegrationUserId,
                vytalStoreGuid,
                fromPath,
              )}
              className="tw:text-xs tw:text-muted-foreground"
            >
              {i18n.t('MerchantTransactions:viewCheckouts')}
            </Link>
          )}
        </div>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:externalIntegrationUserId'),
      },
    });
  }

  if (canAccessUserData) {
    columns.push({
      accessorKey: 'userName',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('Common:userName')}
        />
      ),
      cell: ({ row }) => (
        <Link to={`/users/${row.original.userId}`}>
          {row.original.userName}
        </Link>
      ),
      meta: {
        getName: () => i18n.t('Common:userName'),
      },
    });
  }

  return columns;
}
